// Dashboard.tsx
import React, { useState, useEffect } from "react";
import {
  Users,
  UserCheck,
  UserX,
  Building2,
  Shield,
  Key,
  TrendingUp,
  TrendingDown,
  ChevronRight,
  BarChart3,
  PieChart,
  Activity,
} from "lucide-react";
import { useSelector } from "react-redux";
import type { RootState } from "../../store/store";
import { API_V1_BASE_URL } from "../../config/api";

// Updated Types to match API
interface User {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  status: string;
  role: string;
  role_id: string;
  tenant_id: string;
  tenant_name?: string;
  created_at?: string;
}

interface Role {
  id: string;
  role_name: string;
  description: string;
  permissions: string[];
  is_active: boolean;
}

interface Permission {
  permission_code: string;
  module: string;
  description: string;
}

interface Tenant {
  id: string;
  name: string;
  status: string;
  max_users?: number;
  created_at?: string;
}

interface DashboardStats {
  totalUsers: number;
  activeUsers: number;
  deactivatedUsers: number;
  totalTenants: number;
  activeTenants: number;
  inactiveTenants: number;
  totalRoles: number;
  totalPermissions: number;
}

interface MonthlyData {
  month: string;
  active: number;
  deactivated: number;
}

// Main Dashboard Component
const SuperAdminDashboard: React.FC = () => {
  const { accessToken: token, isLoading: authLoading } = useSelector(
  (state: RootState) => state.auth
);

  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    activeUsers: 0,
    deactivatedUsers: 0,
    totalTenants: 0,
    activeTenants: 0,
    inactiveTenants: 0,
    totalRoles: 0,
    totalPermissions: 0,
  });

  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [monthlyData, setMonthlyData] = useState<MonthlyData[]>([]);
  const [hoveredDataPoint, setHoveredDataPoint] = useState<{
    index: number;
    type: "active" | "deactivated";
  } | null>(null);

  const normalizeArray = (response: any): any[] => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.data?.data)) return response.data.data;
    if (Array.isArray(response?.users)) return response.users;
    if (Array.isArray(response?.roles)) return response.roles;
    if (Array.isArray(response?.permissions)) return response.permissions;
    if (Array.isArray(response?.tenants)) return response.tenants;
    console.warn("Unexpected API response shape:", response);
    return [];
  };

  const formatRoleName = (name: string): string => {
    if (!name) return "";
    return name
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const computeMonthlyGrowth = (usersData: User[]): MonthlyData[] => {
    const months: { month: string; year: number; monthIndex: number }[] = [];
    const now = new Date();
    for (let i = 6; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        month: d.toLocaleString("default", { month: "short" }),
        year: d.getFullYear(),
        monthIndex: d.getMonth(),
      });
    }

    return months.map(({ month, year, monthIndex }) => {
      const usersUpToMonth = usersData.filter((u) => {
        if (!u.created_at) return false;
        const created = new Date(u.created_at);
        return (
          created.getFullYear() < year ||
          (created.getFullYear() === year && created.getMonth() <= monthIndex)
        );
      });
      const active = usersUpToMonth.filter(
        (u) => u.status?.toLowerCase() === "active",
      ).length;
      const deactivated = usersUpToMonth.filter(
        (u) => u.status?.toLowerCase() !== "active",
      ).length;
      return { month, active, deactivated };
    });
  };

  const api = {
    getUsers: async (): Promise<User[]> => {
      const response = await fetch(
        `${API_V1_BASE_URL}/admin/users?limit=1000`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch users");
      }
      const data = await response.json();
      return normalizeArray(data);
    },

    getRoles: async (): Promise<Role[]> => {
      const response = await fetch(`${API_V1_BASE_URL}/admin/roles`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch roles");
      }
      const data = await response.json();
      return normalizeArray(data);
    },

    getPermissions: async (): Promise<Permission[]> => {
      const response = await fetch(
        `${API_V1_BASE_URL}/admin/permissions`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch permissions");
      }
      const data = await response.json();
      return normalizeArray(data);
    },

    getTenants: async (): Promise<Tenant[]> => {
      const response = await fetch(
        `${API_V1_BASE_URL}/admin/tenants`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch tenants");
      }
      const data = await response.json();
      return normalizeArray(data);
    },
  };

  useEffect(() => {
    if (authLoading) return; // wait for auth state to finish loading/rehydrating

    const fetchAllData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!token) {
          throw new Error("No authentication token found. Please login again.");
        }

        const [usersData, rolesData, permissionsData, tenantsData] =
          await Promise.all([
            api.getUsers(),
            api.getRoles(),
            api.getPermissions(),
            api.getTenants(),
          ]);

        const safeUsers = Array.isArray(usersData) ? usersData : [];
        const safeRoles = Array.isArray(rolesData) ? rolesData : [];
        const safePermissions = Array.isArray(permissionsData)
          ? permissionsData
          : [];
        const safeTenants = Array.isArray(tenantsData) ? tenantsData : [];

        setUsers(safeUsers);
        setRoles(safeRoles);
        setPermissions(safePermissions);
        setTenants(safeTenants);

        const activeUsers = safeUsers.filter(
          (u: User) => u.status?.toLowerCase() === "active",
        ).length;
        const deactivatedUsers = safeUsers.filter(
          (u: User) => u.status?.toLowerCase() !== "active",
        ).length;
        const activeTenants = safeTenants.filter(
          (t: Tenant) => t.status?.toLowerCase() === "active",
        ).length;
        const inactiveTenants = safeTenants.filter(
          (t: Tenant) => t.status?.toLowerCase() !== "active",
        ).length;

        setStats({
          totalUsers: safeUsers.length,
          activeUsers,
          deactivatedUsers,
          totalTenants: safeTenants.length,
          activeTenants,
          inactiveTenants,
          totalRoles: safeRoles.length,
          totalPermissions: safePermissions.length,
        });

        setMonthlyData(computeMonthlyGrowth(safeUsers));
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to fetch data");
        console.error("Error fetching dashboard data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
 }, [token, authLoading]);

  const activeUserPercentage =
    stats.totalUsers > 0
      ? Math.round((stats.activeUsers / stats.totalUsers) * 100)
      : 0;

  const deactivatedUserPercentage =
    stats.totalUsers > 0
      ? Math.round((stats.deactivatedUsers / stats.totalUsers) * 100)
      : 0;

  const activeTenantPercentage =
    stats.totalTenants > 0
      ? Math.round((stats.activeTenants / stats.totalTenants) * 100)
      : 0;

  // Updated to return ALL tenants
  const getTopTenants = () => {
    const tenantUserCounts = tenants.map((tenant) => ({
      ...tenant,
      userCount: users.filter((user) => user.tenant_id === tenant.id).length,
    }));
    // Return all tenants sorted by user count
    return tenantUserCounts.sort((a, b) => (b.userCount || 0) - (a.userCount || 0));
  };

  const getRoleDistribution = () => {
    const distribution = roles.map((role) => ({
      ...role,
      count: users.filter((user) => user.role_id === role.id).length,
    }));
    return distribution.sort((a, b) => b.count - a.count);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full">
          <div className="text-red-500 text-center">
            <div className="text-5xl mb-4">⚠️</div>
            <p className="text-xl font-semibold">Error Loading Dashboard</p>
            <p className="mt-2 text-gray-600">{error}</p>
            <div className="mt-6 space-x-3">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                Retry
              </button>
              {error.includes("token") && (
                <button
                  onClick={() => (window.location.href = "/login")}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all duration-200 shadow-md hover:shadow-lg"
                >
                  Go to Login
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 p-6">
      {/* Header with animation */}
      <div className="mb-8 animate-fadeIn">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
              Dashboard Overview
            </h1>
            <p className="text-gray-500 mt-1 flex items-center gap-2">
              <Activity className="w-4 h-4" />A summary of users, tenants, roles
              and permissions across the platform
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="px-4 py-2 bg-blue-50 text-blue-600 rounded-lg text-sm font-medium flex items-center gap-2">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              Live
            </span>
          </div>
        </div>
      </div>

      {/* Stats Cards with hover effects */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6 mb-8">
        <StatCard
          icon={<Users className="w-6 h-6 text-blue-500" />}
          title="Total Users"
          value={stats.totalUsers}
          subtitle={`${stats.activeUsers} active`}
          color="blue"
          trend="+12%"
        />
        <StatCard
          icon={<UserCheck className="w-6 h-6 text-green-500" />}
          title="Active Users"
          value={stats.activeUsers}
          subtitle={`${activeUserPercentage}% of total`}
          color="green"
          trend="+8%"
        />
        <StatCard
          icon={<UserX className="w-6 h-6 text-red-500" />}
          title="Deactivated"
          value={stats.deactivatedUsers}
          subtitle={`${deactivatedUserPercentage}% of total`}
          color="red"
          trend="-2%"
        />
        <StatCard
          icon={<Building2 className="w-6 h-6 text-purple-500" />}
          title="Total Tenants"
          value={stats.totalTenants}
          subtitle={`${stats.activeTenants} active, ${stats.inactiveTenants} inactive`}
          color="purple"
          trend="+5%"
        />
        <StatCard
          icon={<Shield className="w-6 h-6 text-indigo-500" />}
          title="Roles"
          value={stats.totalRoles}
          subtitle="Custom & default"
          color="indigo"
        />
        <StatCard
          icon={<Key className="w-6 h-6 text-yellow-500" />}
          title="Permissions"
          value={stats.totalPermissions}
          subtitle="Accessor & mediator"
          color="yellow"
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* User Growth Line Chart with hover */}
        <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                User Growth
              </h3>
              <p className="text-sm text-gray-500">
                Active vs deactivated users over last 7 months
              </p>
            </div>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
          </div>
          <div className="relative h-64">
            {monthlyData.length > 0 ? (
              <>
                <svg
                  className="w-full h-full"
                  viewBox="0 0 600 250"
                  preserveAspectRatio="none"
                >
                  {/* Grid lines with opacity */}
                  {[0, 50, 100, 150, 200].map((value) => (
                    <line
                      key={value}
                      x1="40"
                      y1={200 - value}
                      x2="580"
                      y2={200 - value}
                      stroke="#E5E7EB"
                      strokeWidth="1"
                      strokeDasharray="4 4"
                      opacity="0.5"
                    />
                  ))}

                  {/* Y-axis labels */}
                  {[0, 50, 100, 150, 200].map((value) => (
                    <text
                      key={value}
                      x="35"
                      y={200 - value + 4}
                      textAnchor="end"
                      className="text-xs fill-gray-400"
                    >
                      {value}
                    </text>
                  ))}

                  {/* Area under active line */}
                  <polygon
                    points={`40,200 ${monthlyData
                      .map((data, index) => {
                        const x = 40 + (index / (monthlyData.length - 1)) * 540;
                        const maxValue = Math.max(
                          ...monthlyData.flatMap((d) => [
                            d.active,
                            d.deactivated,
                          ]),
                        );
                        const y = 200 - (data.active / (maxValue || 1)) * 190;
                        return `${x},${y}`;
                      })
                      .join(" ")} 580,200`}
                    fill="url(#activeGradient)"
                    opacity="0.1"
                  />

                  {/* Active Users Line with glow */}
                  <polyline
                    points={monthlyData
                      .map((data, index) => {
                        const x = 40 + (index / (monthlyData.length - 1)) * 540;
                        const maxValue = Math.max(
                          ...monthlyData.flatMap((d) => [
                            d.active,
                            d.deactivated,
                          ]),
                        );
                        const y = 200 - (data.active / (maxValue || 1)) * 190;
                        return `${x},${y}`;
                      })
                      .join(" ")}
                    fill="none"
                    stroke="#3B82F6"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="transition-all duration-300"
                  />

                  {/* Deactivated Users Line */}
                  <polyline
                    points={monthlyData
                      .map((data, index) => {
                        const x = 40 + (index / (monthlyData.length - 1)) * 540;
                        const maxValue = Math.max(
                          ...monthlyData.flatMap((d) => [
                            d.active,
                            d.deactivated,
                          ]),
                        );
                        const y =
                          200 - (data.deactivated / (maxValue || 1)) * 190;
                        return `${x},${y}`;
                      })
                      .join(" ")}
                    fill="none"
                    stroke="#EF4444"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="transition-all duration-300"
                  />

                  {/* Data points with hover */}
                  {monthlyData.map((data, index) => {
                    const x = 40 + (index / (monthlyData.length - 1)) * 540;
                    const maxValue = Math.max(
                      ...monthlyData.flatMap((d) => [d.active, d.deactivated]),
                    );
                    const activeY = 200 - (data.active / (maxValue || 1)) * 190;
                    const deactiveY =
                      200 - (data.deactivated / (maxValue || 1)) * 190;

                    return (
                      <g key={index}>
                        {/* Active point */}
                        <circle
                          cx={x}
                          cy={activeY}
                          r={
                            hoveredDataPoint?.index === index &&
                              hoveredDataPoint?.type === "active"
                              ? 8
                              : 5
                          }
                          fill="#3B82F6"
                          stroke="white"
                          strokeWidth="3"
                          className="transition-all duration-200 cursor-pointer"
                          onMouseEnter={() =>
                            setHoveredDataPoint({ index, type: "active" })
                          }
                          onMouseLeave={() => setHoveredDataPoint(null)}
                          style={{
                            filter:
                              hoveredDataPoint?.index === index &&
                                hoveredDataPoint?.type === "active"
                                ? "drop-shadow(0 0 8px rgba(59, 130, 246, 0.5))"
                                : "none",
                          }}
                        />
                        {/* Deactivated point */}
                        <circle
                          cx={x}
                          cy={deactiveY}
                          r={
                            hoveredDataPoint?.index === index &&
                              hoveredDataPoint?.type === "deactivated"
                              ? 8
                              : 5
                          }
                          fill="#EF4444"
                          stroke="white"
                          strokeWidth="3"
                          className="transition-all duration-200 cursor-pointer"
                          onMouseEnter={() =>
                            setHoveredDataPoint({ index, type: "deactivated" })
                          }
                          onMouseLeave={() => setHoveredDataPoint(null)}
                          style={{
                            filter:
                              hoveredDataPoint?.index === index &&
                                hoveredDataPoint?.type === "deactivated"
                                ? "drop-shadow(0 0 8px rgba(239, 68, 68, 0.5))"
                                : "none",
                          }}
                        />

                        {/* Tooltip on hover */}
                        {hoveredDataPoint?.index === index && (
                          <>
                            <rect
                              x={x - 35}
                              y={
                                hoveredDataPoint.type === "active"
                                  ? activeY - 45
                                  : deactiveY - 45
                              }
                              width="70"
                              height="30"
                              rx="6"
                              fill="white"
                              stroke="#E5E7EB"
                              strokeWidth="1"
                              className="shadow-lg"
                            />
                            <text
                              x={x}
                              y={
                                hoveredDataPoint.type === "active"
                                  ? activeY - 25
                                  : deactiveY - 25
                              }
                              textAnchor="middle"
                              className="text-xs font-semibold fill-gray-700"
                            >
                              {hoveredDataPoint.type === "active"
                                ? data.active
                                : data.deactivated}
                            </text>
                          </>
                        )}
                      </g>
                    );
                  })}

                  {/* X-axis labels */}
                  {monthlyData.map((data, index) => {
                    const x = 40 + (index / (monthlyData.length - 1)) * 540;
                    return (
                      <text
                        key={index}
                        x={x}
                        y="220"
                        textAnchor="middle"
                        className="text-xs fill-gray-600 font-medium"
                      >
                        {data.month}
                      </text>
                    );
                  })}

                  {/* Gradients */}
                  <defs>
                    <linearGradient
                      id="activeGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.3" />
                      <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                </svg>

                {/* Legend with hover */}
                <div className="flex justify-center space-x-6 mt-2">
                  <div className="flex items-center space-x-2 group cursor-pointer">
                    <div className="w-4 h-0.5 bg-blue-500 group-hover:h-1 transition-all"></div>
                    <span className="text-sm text-gray-600 group-hover:text-blue-600 transition-colors">
                      Active
                    </span>
                    <span className="text-xs text-gray-400">
                      {monthlyData[monthlyData.length - 1]?.active || 0}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2 group cursor-pointer">
                    <div className="w-4 h-0.5 bg-red-400 group-hover:h-1 transition-all"></div>
                    <span className="text-sm text-gray-600 group-hover:text-red-400 transition-colors">
                      Deactivated
                    </span>
                    <span className="text-xs text-gray-400">
                      {monthlyData[monthlyData.length - 1]?.deactivated || 0}
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-gray-400">No data available</p>
              </div>
            )}
          </div>
        </div>

        {/* User Status Pie Chart with hover */}
        <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                User Status
              </h3>
              <p className="text-sm text-gray-500">Active vs deactivated</p>
            </div>
            <PieChart className="w-5 h-5 text-gray-400" />
          </div>
          <div className="flex items-center justify-center">
            <div className="relative w-48 h-48">
              <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                {/* Active segment */}
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="#3B82F6"
                  strokeWidth="20"
                  strokeDasharray={`${(stats.activeUsers / stats.totalUsers) * 251.2} 251.2`}
                  strokeLinecap="round"
                  className="transition-all duration-300 hover:stroke-blue-400 cursor-pointer"
                />
                {/* Deactivated segment */}
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="#EF4444"
                  strokeWidth="20"
                  strokeDasharray={`${(stats.deactivatedUsers / stats.totalUsers) * 251.2} 251.2`}
                  strokeLinecap="round"
                  strokeDashoffset={`-${(stats.activeUsers / stats.totalUsers) * 251.2}`}
                  className="transition-all duration-300 hover:stroke-red-300 cursor-pointer"
                />
              </svg>
              {/* Center text - now properly aligned */}
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-2xl font-bold text-gray-900 leading-none">
                  {stats.totalUsers}
                </span>
                <span className="text-xs text-gray-500 mt-0.5">Total</span>
              </div>
            </div>
            <div className="ml-8 space-y-3">
              <div className="flex items-center space-x-3 group cursor-pointer p-2 rounded-lg hover:bg-blue-50 transition-all duration-200">
                <div className="w-3 h-3 bg-blue-500 rounded-full group-hover:scale-110 transition-transform"></div>
                <div>
                  <span className="text-sm text-gray-600">Active</span>
                  <p className="text-lg font-semibold text-gray-900">
                    {stats.activeUsers}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3 group cursor-pointer p-2 rounded-lg hover:bg-red-50 transition-all duration-200">
                <div className="w-3 h-3 bg-red-400 rounded-full group-hover:scale-110 transition-transform"></div>
                <div>
                  <span className="text-sm text-gray-600">Deactivated</span>
                  <p className="text-lg font-semibold text-gray-900">
                    {stats.deactivatedUsers}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Users by Tenant and Users by Role - Side by Side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Users by Tenant */}
        <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-100 flex flex-col">
          <div className="flex items-center justify-between mb-4 flex-shrink-0">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Users by Tenant
              </h3>
              <p className="text-sm text-gray-500">
                All tenants with user distribution
              </p>
            </div>
            <BarChart3 className="w-5 h-5 text-gray-400 flex-shrink-0" />
          </div>
          <div className="flex-1 min-h-0">
            <div className="space-y-4 h-full max-h-80 overflow-y-auto pr-2 custom-scrollbar">
              {getTopTenants().map((tenant, index) => {
                const colors = [
                  "#3B82F6",
                  "#10B981",
                  "#8B5CF6",
                  "#F59E0B",
                  "#EF4444",
                  "#6B7280",
                  "#EC4899",
                  "#14B8A6",
                  "#F97316",
                  "#6366F1"
                ];
                const userCount = users.filter(
                  (user) => user.tenant_id === tenant.id,
                ).length;
                const topTenants = getTopTenants();
                const maxUserCount = Math.max(
                  ...topTenants.map(
                    (t) => users.filter((user) => user.tenant_id === t.id).length,
                  ),
                  1 // Prevent division by zero
                );
                const percentage =
                  maxUserCount > 0 ? (userCount / maxUserCount) * 100 : 0;

                return (
                  <div key={tenant.id} className="group cursor-pointer">
                    <div className="flex items-center space-x-4">
                      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-sm font-semibold text-gray-600 group-hover:scale-110 transition-transform flex-shrink-0">
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between mb-1">
                          <span className="text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors truncate">
                            {tenant.name}
                          </span>
                          <span className="text-sm text-gray-500 font-medium flex-shrink-0 ml-2">
                            {userCount} users
                          </span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                          <div
                            className="h-2 rounded-full transition-all duration-500 group-hover:opacity-80"
                            style={{
                              width: `${percentage}%`,
                              backgroundColor: colors[index % colors.length],
                              boxShadow: `0 0 10px ${colors[index % colors.length]}40`,
                            }}
                          ></div>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-colors flex-shrink-0" />
                    </div>
                  </div>
                );
              })}

            </div>
          </div>

        </div>

        {/* Users by Role */}
        <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-100 flex flex-col">
          <div className="flex items-center justify-between mb-4 flex-shrink-0">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Users by Role
              </h3>
              <p className="text-sm text-gray-500">
                Distribution of users across roles
              </p>
            </div>
            <Shield className="w-5 h-5 text-gray-400 flex-shrink-0" />
          </div>
          <div className="flex-1 min-h-0">
            <div className="space-y-4 h-full max-h-80 overflow-y-auto pr-2 custom-scrollbar">
              {getRoleDistribution()
                .map((role, index) => {
                  const maxCount = Math.max(
                    ...getRoleDistribution().map((r) => r.count),
                  );
                  const percentage =
                    maxCount > 0 ? (role.count / maxCount) * 100 : 0;
                  const colors = [
                    "#3B82F6",
                    "#10B981",
                    "#8B5CF6",
                    "#F59E0B",
                    "#EF4444",
                    "#6B7280",
                    "#EC4899",
                    "#14B8A6",
                    "#F97316",
                    "#6366F1"
                  ];
                  const colorIndex = index % colors.length;

                  return (
                    <div key={role.id} className="group cursor-pointer">
                      <div className="flex items-center space-x-4 min-h-[2rem]">
                        <div className="w-24 text-sm font-medium text-gray-700 whitespace-normal break-words leading-tight group-hover:text-blue-600 transition-colors flex-shrink-0 self-center">
                          {formatRoleName(role.role_name)}
                        </div>
                        <div className="flex-1 min-w-0 self-center">
                          <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                            <div
                              className="h-3 rounded-full transition-all duration-500 group-hover:opacity-80"
                              style={{
                                width: `${percentage}%`,
                                backgroundColor: colors[colorIndex],
                                boxShadow: `0 0 10px ${colors[colorIndex]}40`,
                              }}
                            ></div>
                          </div>
                        </div>
                        <div className="w-12 text-sm font-medium text-gray-600 text-right group-hover:scale-110 transition-transform flex-shrink-0 self-center">
                          {role.count}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2 flex-shrink-0">
            {roles.slice(0, 6).map((role) => (
              <span
                key={role.id}
                className="px-3 py-1 bg-gradient-to-r from-gray-50 to-gray-100 text-gray-600 text-xs rounded-full font-medium hover:shadow-md transition-all duration-200 cursor-pointer"
              >
                {formatRoleName(role.role_name)}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Tenant Status - Moved below */}
      <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Tenant Status
            </h3>
            <p className="text-sm text-gray-500">Active vs inactive tenants</p>
          </div>
          <Building2 className="w-5 h-5 text-gray-400" />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-8">
            <div className="flex items-center space-x-3 group cursor-pointer p-3 rounded-lg hover:bg-green-50 transition-all duration-200">
              <div className="w-3 h-3 bg-green-500 rounded-full group-hover:scale-110 transition-transform animate-pulse"></div>
              <div>
                <span className="text-sm text-gray-600">Active</span>
                <p className="text-xl font-semibold text-gray-900">
                  {stats.activeTenants}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3 group cursor-pointer p-3 rounded-lg hover:bg-gray-50 transition-all duration-200">
              <div className="w-3 h-3 bg-gray-300 rounded-full group-hover:scale-110 transition-transform"></div>
              <div>
                <span className="text-sm text-gray-600">Inactive</span>
                <p className="text-xl font-semibold text-gray-900">
                  {stats.inactiveTenants}
                </p>
              </div>
            </div>
          </div>
          <div className="relative w-28 h-28">
            <svg
              viewBox="0 0 100 100"
              className="w-full h-full -rotate-90"
            >
              <circle
                cx="50"
                cy="50"
                r="40"
                fill="none"
                stroke="#10B981"
                strokeWidth="15"
                strokeDasharray={`${activeTenantPercentage * 2.512} 251.2`}
                strokeLinecap="round"
                className="transition-all duration-300 hover:stroke-green-400 cursor-pointer"
              />
              <circle
                cx="50"
                cy="50"
                r="40"
                fill="none"
                stroke="#D1D5DB"
                strokeWidth="15"
                strokeDasharray={`${(100 - activeTenantPercentage) * 2.512} 251.2`}
                strokeLinecap="round"
                strokeDashoffset={`-${activeTenantPercentage * 2.512}`}
                className="transition-all duration-300 hover:stroke-gray-400 cursor-pointer"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-xl font-bold text-gray-900 leading-none">{activeTenantPercentage}%</span>
              <span className="text-xs text-gray-500 mt-0.5">Active</span>
            </div>
          </div>
        </div>
        <div className="mt-4 flex justify-center space-x-6">
          <div className="flex items-center space-x-2">
            <div className="w-20 h-1.5 bg-green-500 rounded-full"></div>
            <span className="text-sm text-gray-600">
              {activeTenantPercentage}% Active
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-20 h-1.5 bg-gray-300 rounded-full"></div>
            <span className="text-sm text-gray-600">
              {100 - activeTenantPercentage}% Inactive
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Enhanced Stat Card Component with hover effects
interface StatCardProps {
  icon: React.ReactNode;
  title: string;
  value: number;
  subtitle: string;
  color: string;
  trend?: string;
}

const StatCard: React.FC<StatCardProps> = ({
  icon,
  title,
  value,
  subtitle,
  color,
  trend,
}) => {
  const colorClasses = {
    blue: "border-blue-500 hover:border-blue-600",
    green: "border-green-500 hover:border-green-600",
    red: "border-red-500 hover:border-red-600",
    purple: "border-purple-500 hover:border-purple-600",
    indigo: "border-indigo-500 hover:border-indigo-600",
    yellow: "border-yellow-500 hover:border-yellow-600",
  };

  return (
    <div
      className={`bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border-l-4 ${colorClasses[color as keyof typeof colorClasses]} hover:scale-[1.02] group`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500 uppercase tracking-wider">
            {title}
          </p>
          <p className="text-3xl font-bold text-gray-900 mt-1 group-hover:scale-105 transition-transform origin-left">
            {value}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-xs text-gray-500">{subtitle}</p>
            {trend && (
              <span
                className={`text-xs font-semibold ${trend.startsWith("+") ? "text-green-500" : "text-red-500"} flex items-center gap-0.5`}
              >
                {trend.startsWith("+") ? "↑" : "↓"} {trend}
              </span>
            )}
          </div>
        </div>
        <div
          className={`p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl group-hover:scale-110 transition-transform duration-300 group-hover:shadow-lg`}
        >
          {icon}
        </div>
      </div>
    </div>
  );
};

// Add custom scrollbar styles
const style = document.createElement('style');
style.textContent = `
  .custom-scrollbar::-webkit-scrollbar {
    width: 4px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 10px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;
document.head.appendChild(style);

export default SuperAdminDashboard;
